import React, { FC, useContext, useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '@typings/redux';
import { limits } from 'config/pagination';
import { LocaleContext } from 'context/locale';
import { userPersonalDataChangeRegisterListSelector } from 'store/reports/reports.selector';
import {
  fetchUserPersonalDataChangeRegisterList,
  clearUserPersonalDataChangeRegister,
} from 'store/reports/reports.action';
import { DataGrid } from 'components/shared/data/DataGrid/DataGrid';
import { Paper } from 'components/shared/surfaces/Paper/Paper';
import { ColumnsConfig } from 'components/shared/data/DataGrid/DataGrid.types';
import { useStyles } from 'components/childRegister/Editor/EducationPath/EducationPath.styles';
import { DataGridApi } from 'components/shared/data/DataGrid/DataGrid.types';
import {
  UserPersonalDataChanges,
  FieldChange,
  OriginRole,
  UserPersonalDataChangeAction,
  FieldName,
} from '../AccessList.types';
import { Toolbar } from './Toolbar';
import { focusField } from 'utils/form';

export const UserPersonalDataChangeRegisterList: FC = () => {
  const { trans } = useContext(LocaleContext);
  const dispatch = useDispatch<AppDispatch>();
  const { list, total, filters } = useSelector(userPersonalDataChangeRegisterListSelector);
  const classes = useStyles();
  const toolbarRef = useRef(null);

  const [gridApi, setGridApi] = useState<DataGridApi>(null);

  useEffect(() => {
    dispatch(clearUserPersonalDataChangeRegister());
  }, [dispatch]);

  useEffect(() => {
    if (gridApi) {
      gridApi.resetPagination();
    }
  }, [filters, gridApi]);

  const getActionName = (actionName: UserPersonalDataChangeAction) => {
    if (
      actionName === UserPersonalDataChangeAction.CREATE_APPLICANT ||
      actionName === UserPersonalDataChangeAction.CREATE_OFFICIAL
    ) {
      return trans('PERSONAL_DATA_ACCESS.DATA_CHANGE_REGISTER_LIST.ACTION_NAME.CREATE');
    } else if (
      actionName === UserPersonalDataChangeAction.UPDATE_APPLICANT ||
      actionName === UserPersonalDataChangeAction.UPDATE_OFFICIAL
    ) {
      return trans('PERSONAL_DATA_ACCESS.DATA_CHANGE_REGISTER_LIST.ACTION_NAME.UPDATE');
    } else if (
      actionName === UserPersonalDataChangeAction.REMOVE_APPLICANT ||
      actionName === UserPersonalDataChangeAction.REMOVE_OFFICIAL
    ) {
      return trans('PERSONAL_DATA_ACCESS.DATA_CHANGE_REGISTER_LIST.ACTION_NAME.REMOVE');
    } else {
      return '';
    }
  };

  const getFieldName = (name: FieldName) => {
    if (name === FieldName.FIRST_NAME) {
      return trans('USER_PERSONAL_DATA_CHANGE_REGISTER_LIST.FIELD_NAME.FIRST_NAME');
    } else if (name === FieldName.LAST_NAME) {
      return trans('USER_PERSONAL_DATA_CHANGE_REGISTER_LIST.FIELD_NAME.LAST_NAME');
    } else if (name === FieldName.EMAIL) {
      return trans('USER_PERSONAL_DATA_CHANGE_REGISTER_LIST.FIELD_NAME.EMAIL');
    } else {
      return name;
    }
  };

  const getChangedFieldsList = (changes: FieldChange[], actionName: UserPersonalDataChangeAction) => {
    if (
      changes.length > 0 &&
      (actionName === UserPersonalDataChangeAction.UPDATE_APPLICANT ||
        actionName === UserPersonalDataChangeAction.UPDATE_OFFICIAL)
    ) {
      const changedFields = [];
      changes.forEach((item, index) => {
        changedFields.push(
          ` ${getFieldName(item.fieldName)} (${item.oldValue} => ${item.newValue})${
            index < changes.length - 1 ? '' : '.'
          }`,
        );
      });
      return `Zmieniono pola: ${changedFields.toString()}`;
    } else {
      return '';
    }
  };

  const getColumns: ColumnsConfig<UserPersonalDataChanges, void> = () => [
    {
      field: 'changeDate',
      name: 'PERSONAL_DATA_ACCESS.DATA_CHANGE_REGISTER_LIST.COLUMNS.CHANGE_DATE',
      columnParams: { minWidth: 130 },
      format: (data, trans, formatter) => {
        return formatter.formatDateWithTime(data.changeDate);
      },
    },
    {
      field: 'userLogin',
      name: 'PERSONAL_DATA_ACCESS.DATA_CHANGE_REGISTER_LIST.COLUMNS.USER_LOGIN',
      format: (data) => `${data.userLogin} / ${data.userName}`,
    },
    {
      field: 'userIp',
      name: 'PERSONAL_DATA_ACCESS.DATA_CHANGE_REGISTER_LIST.COLUMNS.USER_IP',
      columnParams: { minWidth: 110 },
      format: (data) => data.userIp,
    },
    {
      field: 'lastName',
      name: 'PERSONAL_DATA_ACCESS.DATA_CHANGE_REGISTER_LIST.COLUMNS.LAST_NAME',
      format: (data) => `${data.firstName} ${data.lastName}`,
    },
    {
      field: 'origin',
      name: 'PERSONAL_DATA_ACCESS.DATA_CHANGE_REGISTER_LIST.COLUMNS.ORIGIN',
      format: (data) =>
        data.origin === OriginRole.OFFICIAL
          ? trans('PERSONAL_DATA_ACCESS.DATA_CHANGE_REGISTER_LIST.COLUMNS.ORIGIN.OFFICIAL')
          : trans('PERSONAL_DATA_ACCESS.DATA_CHANGE_REGISTER_LIST.COLUMNS.ORIFIN.APPLICANT'),
    },
    {
      field: 'action',
      name: 'PERSONAL_DATA_ACCESS.DATA_CHANGE_REGISTER_LIST.COLUMNS.ACTION',
      columnParams: { flex: 1, minWidth: 280 },
      format: (data) => `${getActionName(data.action)} ${getChangedFieldsList(data.changesRange, data.action)}`,
    },
  ];

  const onDataFetch = useCallback(
    (page) => dispatch(fetchUserPersonalDataChangeRegisterList(page, filters)),
    [dispatch, filters],
  );
  const onDataProvide = useCallback((page) => list[page], [list]);

  const onGridReadyHandler = useCallback(
    (grid: DataGridApi) => {
      setGridApi(grid);
    },
    [setGridApi],
  );

  const onFocusBeforeGridHandler = useCallback(() => {
    if (toolbarRef.current) {
      // using setTimeout to wait over the tab event that we can't prevent
      setTimeout(() => focusField(toolbarRef.current, 'studentRegister'));
    }
  }, [toolbarRef]);

  const onFocusAfterGridHandler = () => {
    setTimeout(() => document.getElementById('sidebar-button').focus());
  };

  const gridFocusHandler = () => {
    gridApi && gridApi.focusCell(0, 0);
  };

  return (
    <Paper innerSpacing={2} square={true} className={classes.container}>
      <Toolbar onGridFocus={gridFocusHandler} ref={toolbarRef} />
      <DataGrid<UserPersonalDataChanges>
        columns={getColumns()}
        onDataProvide={onDataProvide}
        onDataFetch={onDataFetch}
        total={total}
        limit={limits.personalDataAccessList}
        loadingMessage={'COMMON.LOADING_DATA'}
        noResultsMessage={'COMMON.NOTHING-FOUND'}
        onGridReady={onGridReadyHandler}
        onFocusBeforeGrid={onFocusBeforeGridHandler}
        onFocusAfterGrid={onFocusAfterGridHandler}
      />
    </Paper>
  );
};
